import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import AKAvatar from '@atlaskit/avatar';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { Box, xcss } from '@atlaskit/primitives';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';
import { VARIATION } from '@atlassian/jira-common-util-get-experiment-group/src/index.tsx';
import { useRealtimePresenceExperiment } from '@atlassian/jira-real-time-presence/src/controllers/use-realtime-presence-experiment/index.tsx';
import { type IntlShape, injectIntl } from '@atlassian/jira-intl';
import type { AccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { getIsSoftwareThemingEnabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { useSoftwareProjectTheming } from '@atlassian/jira-project-theme-providers/src/controllers/software-theme/index.tsx';
import PresenceTooltip from '../../../../../common/ui/presence-tooltip/index.tsx';
import type { Person } from '../../../../../common/types.tsx';
import {
	AvatarBorder,
	AvatarWrapper,
	hiddenCheckboxStyles,
} from '../../../../../common/ui/assignee/index.tsx';
import { onBlur, useOnKeyPress } from '../../../../../common/ui/assignee/utils.tsx';
import messages from './messages.tsx';

export type Props = {
	isSelected?: boolean;
	user: Person;
	zIndex: number;
	onToggle: (userId: AccountId) => void;
	intl: IntlShape;
	isDisabled?: boolean;
};

const type = 'assignee';

const Avatar = (props: Props) => {
	const { user, isSelected, zIndex, intl, onToggle: onToggleFromProps, isDisabled = false } = props;

	const { formatMessage } = intl;
	const { isSoftwareFullTheming } = getIsSoftwareThemingEnabled()
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useSoftwareProjectTheming()
		: { isSoftwareFullTheming: false };

	const userIdentifier = user.id;

	const checkboxId = `${type}-${userIdentifier}`;

	const pressTrace = usePressTracing('filters.assignee-filter.avatar');

	const onToggle = useCallback(() => {
		onToggleFromProps(user.id);

		if (fg('fix_blindspots_for_board_and_backlog')) {
			pressTrace();
		}
	}, [onToggleFromProps, user.id, pressTrace]);

	const onKeyPress = useOnKeyPress(onToggle);

	const canAppear = useCallback(() => !isDisabled, [isDisabled]);

	const { experimentCohort } = useRealtimePresenceExperiment();

	const isRealtimePresenceEnabled = experimentCohort === VARIATION;

	const altTextWithPresence = formatMessage(
		user.presence === 'online' ? messages.presenceOnlineLabel : messages.presenceOfflineLabel,
		{ name: user.displayName },
	);

	const avatarWithTooltip = isRealtimePresenceEnabled ? (
		<PresenceTooltip content={user.displayName} presence={user.presence} tag="span">
			<AKAvatar
				testId="filters.ui.filters.assignee.stateless.avatar.ak-avatar"
				size={isVisualRefreshEnabled() ? 'small' : 'medium'}
				as="span"
				src={user.avatarUrl}
				name={user.presence ? altTextWithPresence : user.displayName}
				presence={user.presence}
				{...(fg('list_lovability_improving_filters') && { isDisabled })}
			/>
		</PresenceTooltip>
	) : (
		<Tooltip
			content={user.displayName}
			tag="span"
			canAppear={fg('list_lovability_improving_filters') ? canAppear : undefined}
		>
			<AKAvatar
				testId="filters.ui.filters.assignee.stateless.avatar.ak-avatar"
				size={isVisualRefreshEnabled() ? 'small' : 'medium'}
				as="span"
				src={user.avatarUrl}
				name={user.displayName}
				presence={user.presence}
				{...(fg('list_lovability_improving_filters') && { isDisabled })}
			/>
		</Tooltip>
	);

	const hideSelectionWhenDisabled = isDisabled ? false : isSelected;

	return (
		<AvatarWrapper
			selected={fg('list_lovability_improving_filters') ? hideSelectionWhenDisabled : isSelected}
			zIndex={zIndex}
			{...(fg('list_lovability_improving_filters') && { isDisabled })}
		>
			<AvatarInputCheckbox
				aria-label={formatMessage(messages.inputCheckboxLabel, { username: user.displayName })}
				type="checkbox"
				id={checkboxId}
				name={type}
				value={userIdentifier}
				defaultChecked={
					fg('list_lovability_improving_filters') ? hideSelectionWhenDisabled : isSelected
				}
				onChange={onToggle}
				onKeyPress={onKeyPress}
				onClick={onBlur}
				{...(fg('list_lovability_improving_filters') && {
					disabled: isDisabled,
					isDisabled,
					'aria-disabled': isDisabled,
				})}
			/>
			<AvatarBorder
				selected={fg('list_lovability_improving_filters') ? hideSelectionWhenDisabled : isSelected}
				isSoftwareFullTheming={isSoftwareFullTheming}
				aria-hidden
			>
				<Box
					as="label"
					testId="filters.ui.filters.assignee.stateless.avatar.assignee-filter-avatar"
					htmlFor={checkboxId}
					xcss={[
						isVisualRefreshEnabled() ? avatarLabelBaseWithVrStyles : avatarLabelBaseStyles,
						isDisabled && fg('list_lovability_improving_filters') && withDisabledCursorStyles,
					]}
				>
					{avatarWithTooltip}
				</Box>
			</AvatarBorder>
		</AvatarWrapper>
	);
};

Avatar.defaultProps = {
	isSelected: false,
	onToggle: noop,
};

export default injectIntl(Avatar);

const avatarLabelBaseStyles = xcss({
	height: '36px',
	display: 'block',
	cursor: 'pointer',
});

const withDisabledCursorStyles = xcss({
	cursor: 'not-allowed',
});

const avatarLabelBaseWithVrStyles = xcss({
	height: '28px',
	display: 'block',
	cursor: 'pointer',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const AvatarInputCheckbox = styled.input<{ isDisabled?: boolean }>(hiddenCheckboxStyles, {
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	cursor: ({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&:focus + div': {
		borderColor: token('color.text.selected'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const visibleForTesting = {
	AvatarInputCheckbox,
};
